import InputLabel from "@mui/material/InputLabel";
import GroupIcon from "@mui/icons-material/Group";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { Breadcrumbs } from "components/common/Breadcrumbs";
import { Breadcrumb } from "components/common/Breadcrumbs/types";
import { FormDialog } from "components/common/FormDialog";
import {
  FIELD_TYPES,
  FormDialogProps
} from "components/common/FormDialog/types";
import { Head } from "components/common/Head";
import { Table } from "components/common/Table";
import {
  TABLE_SORTING_TYPES,
  TableColumn,
  TableRowActionsMenuItem
} from "components/common/Table/types";
import { format, startOfMonth, sub } from "date-fns";
import dayjs, { Dayjs } from "dayjs";
import { useMount } from "hooks/useMount";
import { usePrevious } from "hooks/usePrevious";
import { useUnmount } from "hooks/useUnmount";
import { billingActions } from "modules/billing/slices";
import { pollingActions } from "modules/polling/slices";
import {
  isOrganizationBillingDataLoadingSelector,
  organizationBillingDataSelector,
  tableOrganizationProjectsBillingDataSelector,
  tableOrganizationTotalBillingDataSelector
} from "modules/billing/selectors";
import {
  BILLING_ROLES,
  BILLING_ROLES_LABELS,
  TableOrganizationProjectBillingData
} from "modules/billing/types";
import { enterprisesActions } from "modules/enterprises/slices";
import { isBillingAdminsListUpdatingSelector } from "modules/enterprises/selectors";
import { notificationsActions } from "modules/notifications/slices";
import { NOTIFICATION_TYPES } from "modules/notifications/types";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { generatePath, useParams } from "react-router-dom";
import { downloadFile } from "utils/downloadFile";
import { API_PATH } from "../../axios";
import {
  DATE_FORMATS,
  DEFAULT_CURRENCY,
  ERROR_MESSAGES,
  PROJECT_STATUS_LABELS,
  REGEX,
  ROUTES
} from "../../constants";
import * as s from "./styles";
import { DATE_TYPES, DIALOG_TYPES, PROJECT_STATUSES } from "./types";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { array, string } from "yup";
import { IconButton, Tooltip } from "@mui/material";
import { Loader } from "components/common/Loader";

const tableColumns: TableColumn<TableOrganizationProjectBillingData>[] = [
  { key: "name", label: "Project Name" },
  { key: "region", label: "Region" },
  { key: "status", label: "Status" }
];

const POLL_ID_PREFIX = "ORG_BILLING";
const POLL_IDS = {
  organization_billing: "ORGANIZATION_BILLING"
};

const CURRENCY_SELECT_ID = "currency";

const title = "Billing";

export const BillingOrganization: FC = () => {
  const dispatch = useDispatch();
  const matchParams = useParams<{
    organizationId: string;
  }>();
  const billingData = useSelector(organizationBillingDataSelector);
  const isBillingDataLoading = useSelector(
    isOrganizationBillingDataLoadingSelector
  );
  const tableBillingData = useSelector(
    tableOrganizationProjectsBillingDataSelector
  );
  const tableBillingDataTotal = useSelector(
    tableOrganizationTotalBillingDataSelector
  );

  const isBillingAdminsListUpdating = useSelector(
    isBillingAdminsListUpdatingSelector
  );
  const isOperationInProgress = isBillingAdminsListUpdating;
  const previousIsOperationInProgress = usePrevious(isOperationInProgress);

  const currentDate = useMemo(() => new Date(), []);
  const [minDate] = useState<Dayjs>(() =>
    dayjs(sub(currentDate, { days: 120 })).startOf("day")
  );

  const [startDate, setStartDate] = useState<Dayjs | null>(() =>
    dayjs(startOfMonth(currentDate)).startOf("day")
  );

  const [endDate, setEndDate] = useState<Dayjs | null>(() =>
    dayjs(currentDate).startOf("day")
  );

  const startDateStr = startDate?.format(DATE_FORMATS.DAYJS_ISO_DATE) ?? null;
  const endDateStr = endDate?.format(DATE_FORMATS.DAYJS_ISO_DATE) ?? null;

  const [currency, setCurrency] = useState<string>(DEFAULT_CURRENCY);

  const currencies = useMemo(
    () => (billingData ? Object.keys(billingData.costs) : ["chf", "eur"]),
    [billingData]
  );

  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const previousSelectedItemId = usePrevious(selectedItemId);
  const currentItemId = selectedItemId
    ? selectedItemId
    : previousSelectedItemId;
  const currentProjectRegion = billingData?.projects
    ?.find((project) => project.id === currentItemId)
    ?.region.toLowerCase();
  const currentProjectName = billingData?.projects?.find(
    (project) => project.id === currentItemId
  )?.name;

  const orgName = billingData?.name || "";
  const orgRole = billingData?.billing_role
    ? BILLING_ROLES_LABELS[billingData.billing_role]
    : "";
  // const orgBillingAdmins = billingData?.billing_admins.join(", ") || "";
  // const hasOnlyOwner = !billingData?.billing_admins.length;

  const [dialog, setDialog] = useState<{
    isOpened: boolean;
    type: DIALOG_TYPES;
  }>({ type: DIALOG_TYPES.GENERATE_BILLING_REPORT, isOpened: false });

  const handleCloseDialog = useCallback(() => {
    setDialog({
      ...dialog,
      isOpened: false
    });
    setSelectedItemId(null);
  }, [dialog]);

  const handleGenerateBillingReportButtonClick = useCallback((id: string) => {
    setSelectedItemId(id);
    setDialog({
      type: DIALOG_TYPES.GENERATE_BILLING_REPORT,
      isOpened: true
    });
  }, []);

  const handleUpdateBillingAdminsListMenuItemClick = useCallback(() => {
    setDialog({
      type: DIALOG_TYPES.UPDATE_BILLING_ADMINS_LIST,
      isOpened: true
    });
  }, []);

  const breadcrumbs: Breadcrumb[] = [
    {
      text: "Billing",
      url: generatePath(ROUTES.BILLING_ORGANIZATIONS)
    },
    {
      text: billingData?.name || "",
      url: generatePath(ROUTES.BILLING_ORGANIZATION, {
        organizationId: matchParams.organizationId
      })
    }
  ];

  const tableActions: TableRowActionsMenuItem<TableOrganizationProjectBillingData>[] =
    [
      {
        label: "Generate billing report",
        handler: handleGenerateBillingReportButtonClick
        // isDisabled: (project) => project.status !== "Active"
      }
    ];

  const generateTableItemURL = useCallback(
    (id: string) =>
      generatePath(ROUTES.BILLING_PROJECT, {
        organizationId: matchParams.organizationId,
        regionId: billingData?.projects
          ?.find((project) => project.id === id)
          ?.region.toLowerCase(),
        projectId: id
      }),
    [billingData, matchParams.organizationId]
  );

  const handleConfirmDownloadButtonClick = useCallback(
    (data: { start_date: string; end_date: string }) => {
      if (selectedItemId) {
        const startDate = format(
          new Date(data.start_date),
          DATE_FORMATS.ISO_DATE
        );
        const endDate = format(new Date(data.end_date), DATE_FORMATS.ISO_DATE);
        downloadFile(
          `${API_PATH}gotham-wayne-tower/method/billing/organizations/${matchParams.organizationId!}/regions/${currentProjectRegion!}/projects/${selectedItemId}/download?start=${startDate}&end=${endDate}`
        );
        dispatch(
          notificationsActions.showNotification({
            title: "Billing report is generating. Download will start soon.",
            type: NOTIFICATION_TYPES.INFO
          })
        );
      }
      handleCloseDialog();
    },
    [
      selectedItemId,
      matchParams.organizationId,
      currentProjectRegion,
      dispatch,
      handleCloseDialog
    ]
  );

  const handleConfirmUpdateBillingAdminsList = useCallback(
    (data: { admins: string[] }) => {
      dispatch(
        enterprisesActions.updateBillingAdminsListRequest({
          orgId: matchParams.organizationId!,
          admins: data.admins
        })
      );

      handleCloseDialog();
    },
    [dispatch, matchParams.organizationId, handleCloseDialog]
  );

  useMount(() => {
    if (startDateStr && endDateStr) {
      dispatch(
        billingActions.getOrganizationBillingDataRequest({
          startDate: startDateStr,
          endDate: endDateStr,
          id: matchParams.organizationId!
        })
      );
      // dispatch(
      //   pollingActions.startPolling({
      //     id: `${POLL_ID_PREFIX}/${POLL_IDS.organization_billing}`,
      //     action: billingActions.getOrganizationBillingDataRequest({
      //       startDate: startDateStr,
      //       endDate: endDateStr,
      //       id: matchParams.organizationId!
      //     })
      //   })
      // );
    }
  });

  useUnmount(() => {
    Object.values(POLL_IDS).forEach((id) => {
      dispatch(
        pollingActions.stopPolling({
          id: `${POLL_ID_PREFIX}/${id}`
        })
      );
    });
    dispatch(enterprisesActions.clear());
    dispatch(billingActions.clear());
  });

  useEffect(() => {
    if (startDateStr && endDateStr) {
      dispatch(
        billingActions.getOrganizationBillingDataRequest({
          startDate: startDateStr,
          endDate: endDateStr,
          id: matchParams.organizationId!
        })
      );
    }
  }, [dispatch, startDateStr, endDateStr, matchParams.organizationId]);

  useEffect(() => {
    if (previousIsOperationInProgress && !isOperationInProgress) {
      if (startDateStr && endDateStr) {
        dispatch(
          billingActions.getOrganizationBillingDataRequest({
            startDate: startDateStr,
            endDate: endDateStr,
            id: matchParams.organizationId!
          })
        );
      }
    }
  }, [
    previousIsOperationInProgress,
    isOperationInProgress,
    dispatch,
    startDateStr,
    endDateStr,
    matchParams.organizationId
  ]);

  const handleDateChange = useCallback(
    (dateType: DATE_TYPES, value: Dayjs | null) => {
      switch (dateType) {
        case DATE_TYPES.START:
          setStartDate(value);
          break;
        case DATE_TYPES.END:
          setEndDate(value);
          break;
      }
    },
    []
  );

  const handleCurrencyChange = useCallback((event: SelectChangeEvent) => {
    setCurrency(event.target.value);
  }, []);

  const dialogProps: {
    [key in DIALOG_TYPES]: Omit<FormDialogProps, "isOpened" | "onCancel">;
  } = {
    [DIALOG_TYPES.GENERATE_BILLING_REPORT]: {
      title: `Generate billing report for the project "${currentProjectName}"`,
      // onConfirm: handleConfirmGenerateButtonClick,
      // confirmButtonLabel: "Generate",
      onConfirm: handleConfirmDownloadButtonClick,
      confirmButtonLabel: "Download",
      fields: [
        {
          name: "start_date",
          type: FIELD_TYPES.DATE_PICKER,
          label: "Start-date",
          minDate: minDate,
          disableFuture: true,
          defaultValue: startDate
        },
        {
          name: "end_date",
          type: FIELD_TYPES.DATE_PICKER,
          label: "End-date",
          disableFuture: true,
          defaultValue: endDate
        },
        {
          name: "notes",
          type: FIELD_TYPES.LABEL,
          label: `ℹ️ Billing data is available for previous 120 days`
        }
      ]
    },
    [DIALOG_TYPES.UPDATE_BILLING_ADMINS_LIST]: {
      title: `Update billing administrators`,
      onConfirm: handleConfirmUpdateBillingAdminsList,
      confirmButtonLabel: "Update",
      fields: [
        {
          name: "admins",
          type: FIELD_TYPES.MULTISELECT,
          label: "E-mails of billing administrators",
          // variant: "outlined",
          options: [],
          freeSolo: true,
          defaultValue: billingData?.billing_admins ?? [],
          rules: array()
            .of(
              string().matches(
                REGEX.EMAIL_ADDRESS,
                ERROR_MESSAGES.EMAIL_ADDRESS
              )
            )
            .max(5, "You can add up to 5 billing administrators")
            .default([]),
          helperText: 'Press "Enter" to add a new admin'
        },
        {
          name: "info",
          type: FIELD_TYPES.LABEL,
          label: `\nℹ️ Only already registered users can be assigned as billing administrators.\n\n`
        }
      ]
    }
  };

  return (
    <>
      <Head title={title} />
      <Breadcrumbs breadcrumbs={breadcrumbs} />

      {!billingData || isBillingAdminsListUpdating ? (
        // 🔄 Loading
        <s.StubContainer>
          <Loader text="Loading data..." />
        </s.StubContainer>
      ) : [BILLING_ROLES.OWNER, BILLING_ROLES.ADMIN].includes(
          billingData.billing_role
        ) ? (
        // ✅ Has access
        <>
          <s.SummaryContainer>
            <s.Title variant={"h4"} component={"h2"}>
              {orgName}: {title}
            </s.Title>
            {[BILLING_ROLES.OWNER].includes(billingData?.billing_role) && (
              <s.ActionsContainer>
                <Tooltip title={"Update billing admins list"} arrow>
                  <span>
                    <IconButton
                      onClick={handleUpdateBillingAdminsListMenuItemClick}
                      color={"inherit"}
                      disabled={!billingData}
                    >
                      <GroupIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </s.ActionsContainer>
            )}
          </s.SummaryContainer>
          <s.SummaryRow>
            <s.DetailsTitle>ID: </s.DetailsTitle>
            <s.DetailsInfoColored>{billingData.id}</s.DetailsInfoColored>
          </s.SummaryRow>
          <s.SummaryRow>
            <s.DetailsTitle>User role: </s.DetailsTitle>
            <s.Tag label={orgRole} />
            {/* <s.DetailsInfoColored>{orgRole}</s.DetailsInfoColored> */}
            {billingData.billing_role === BILLING_ROLES.ADMIN && (
              <>
                <s.DetailsTitle>Owner: </s.DetailsTitle>
                <s.Tag label={billingData.owner} />
              </>
            )}
          </s.SummaryRow>
          <s.SummaryRow>
            <s.DetailsTitle>Billing admins:</s.DetailsTitle>

            {(billingData?.billing_admins?.length ?? 0) === 0 ? (
              <s.DetailsInfoGray>
                no additional billing admins
              </s.DetailsInfoGray>
            ) : (
              billingData.billing_admins.map((email) => (
                <s.Tag key={email} label={email} />
              ))
            )}
          </s.SummaryRow>
          <s.ProjectsContainer>
            <s.Description>
              ℹ️ This billing data is only for the initial reference. The final
              costs will be provided in the invoice.
            </s.Description>
            <s.Description2>
              ℹ️ Billing data is available for the last 120 days
            </s.Description2>

            <Table
              isSearchEnabled
              actions={tableActions}
              isSortingEnabled
              rows={tableBillingData || []}
              columns={[
                ...tableColumns,
                {
                  key: `cost-${currency}`,
                  label: "Cost",
                  sortingType: TABLE_SORTING_TYPES.NUMBER
                }
              ]}
              totalRow={tableBillingDataTotal || undefined}
              itemLink={{
                column: "name",
                getURL: generateTableItemURL,
                isEnabled: (row) =>
                  row.status !== PROJECT_STATUS_LABELS[PROJECT_STATUSES.DELETED]
              }}
              isLoading={isBillingDataLoading}
              toolbarItems={
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    name="start"
                    label="Start date"
                    onChange={(value) =>
                      handleDateChange(DATE_TYPES.START, value as Dayjs | null)
                    }
                    value={startDate || null}
                    format="DD MMM YYYY"
                    minDate={minDate}
                    maxDate={endDate || undefined}
                    disableFuture
                    sx={{ marginRight: "10px" }}
                    slotProps={{ textField: { size: "small" } }}
                  />

                  <DatePicker
                    name="end"
                    label="End date"
                    onChange={(value) =>
                      handleDateChange(DATE_TYPES.END, value as Dayjs | null)
                    }
                    value={endDate || null}
                    format="DD MMM YYYY"
                    minDate={startDate || minDate}
                    disableFuture
                    sx={{ marginRight: "10px" }}
                    slotProps={{ textField: { size: "small" } }}
                  />

                  {currencies.length > 0 && (
                    <s.CurrencyFormControl>
                      <InputLabel id={CURRENCY_SELECT_ID}>Currency</InputLabel>
                      <Select
                        label="Currency"
                        labelId={CURRENCY_SELECT_ID}
                        value={currency}
                        onChange={handleCurrencyChange}
                        size="small"
                      >
                        {currencies.map((currency) => (
                          <MenuItem key={currency} value={currency}>
                            {currency.toUpperCase()}
                          </MenuItem>
                        ))}
                      </Select>
                    </s.CurrencyFormControl>
                  )}
                </LocalizationProvider>
              }
            />

            <FormDialog
              isOpened={dialog.isOpened}
              onCancel={handleCloseDialog}
              fields={dialogProps[dialog.type].fields}
              onConfirm={dialogProps[dialog.type].onConfirm}
              title={dialogProps[dialog.type].title}
              confirmButtonLabel={dialogProps[dialog.type].confirmButtonLabel}
            />
          </s.ProjectsContainer>
        </>
      ) : (
        // 🔒 No permissions
        <s.StubContainer>
          <s.Description style={{ textAlign: "center" }}>
            🔒 You don’t have permission to view billing for this organization
            and its projects.
          </s.Description>
          <s.Description style={{ textAlign: "center" }}>
            Please contact the organization owner to request access.
          </s.Description>
        </s.StubContainer>
      )}
    </>
  );
};
